import { useMemo, useState } from "react";

import { Table } from "@similarweb/pro-logic-components";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { countryByIdFilter } from "app/filters/ngFilters";
import { useTranslation } from "pro-features/components/WithTranslation";

import { useCategoriesComparison } from "../../categories-context";
import { getQueryParams } from "../../common/getQueryParams";
import { ENDPOINT_EXCEL } from "../../overview/widgets/top-countries/consts";
import { getComparisonTableColumnsConfig } from "../config/getComparisonTableColumnsConfig";
import { useGeographyComparisonData } from "../hooks/geographyQueries";
import { buildComparisonTableData } from "../utils/dataProcessors";

import { TableSearchAndDownloadHeader } from "./TableSearchAndDownloadHeader";

const getCountryById = countryByIdFilter();

export const IndustryAnalysisGeographyComparisonTable = () => {
    const t = useTranslation();
    const subtitleFilters = useSubtitleFilters("date", "webSource");
    const { categories } = useCategoriesComparison();
    const { data, isLoading, isError } = useGeographyComparisonData(categories || []);
    const [searchString, setSearchString] = useState("");

    const tableData = useMemo(() => {
        const rawData = buildComparisonTableData(data);

        if (!searchString) {
            return rawData;
        }

        const regex = new RegExp(searchString, "i");
        return rawData.filter((row) => regex.test(getCountryById(row.country).text));
    }, [data, searchString]);

    const onSearch = (val: string) => {
        setSearchString(val.trim());
    };

    const columnConfig = useMemo(() => {
        return getComparisonTableColumnsConfig(categories);
    }, [categories]);

    const getExcelUrl = () => {
        const paramsObject = new URLSearchParams();
        const params = {
            ...getQueryParams(),
            includeSubDomains: true,
        };
        for (const [key, value] of Object.entries(params)) {
            if (Array.isArray(value)) {
                value.forEach((item) => {
                    paramsObject.append(key, String(item));
                });
            } else if (value) {
                paramsObject.append(key, String(value));
            }
        }
        categories.forEach((category) => paramsObject.append("keys", category.category.forApi));
        const query = paramsObject.toString();
        return `${ENDPOINT_EXCEL}?${query}`;
    };

    return (
        <WidgetContainer
            withBorder
            title={t("industry.analysis.geo.overtime.graph.title")}
            tooltip={t("industry.analysis.geo.overtime.graph.title.tooltip")}
            subtitleFilters={subtitleFilters}
            noData={isError}
            automationId="industry-analysis-geography-comparison-table"
        >
            <TableSearchAndDownloadHeader
                searchValue={searchString}
                onSearch={onSearch}
                downloadUrl={getExcelUrl()}
            />
            <Table
                isLoading={isLoading}
                loaderRowCount={10}
                sticky={false}
                data={tableData}
                columnConfig={columnConfig}
                pagination={{ pageSize: 125 }}
            />
        </WidgetContainer>
    );
};
